import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import BrandLogo from '@/components/BrandLogo';
import {
  LayoutDashboard, Users, Settings, LogOut, Bell,
  ChevronDown, FileText, TrendingUp, Truck, Tag, Globe2,
  MessageSquare, BarChart2, Layers, BookOpen, UserCog, FileUp, ClipboardCheck
} from 'lucide-react';
import { useState } from 'react';

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
        isActive
          ? 'bg-slate-100 text-slate-900 font-semibold'
          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
      }`
    }
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    {label}
  </NavLink>
);

const SectionLabel = ({ label }) => (
  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 px-3 py-1.5 mt-2">{label}</p>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = (user?.name || 'A').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 h-screen fixed top-0 left-0 flex flex-col z-20 overflow-y-auto">
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <BrandLogo variant="mark" className="max-w-[170px]" />
            <span className="shrink-0 rounded bg-slate-900 px-1.5 py-0.5 text-xs font-medium text-white">Admin</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          <NavItem to="/admin/dashboard" icon={LayoutDashboard} label="Dashboard" testId="admin-nav-dashboard" />

          <SectionLabel label="Customers" />
          <NavItem to="/admin/customers" icon={Users} label="Customers" testId="admin-nav-customers" />
          <NavItem to="/admin/crm" icon={BarChart2} label="CRM Tracking" testId="admin-nav-crm" />

          <SectionLabel label="Platforms" />
          <NavItem to="/admin/trade-activity" icon={Layers} label="Trade activity" testId="admin-nav-trade-activity" />
          <NavItem to="/admin/rfqs" icon={FileText} label="RFQ Management" testId="admin-nav-rfqs" />
          <NavItem to="/admin/commodity" icon={TrendingUp} label="CommodityBid" testId="admin-nav-commodity" />
          <NavItem to="/admin/freight" icon={Truck} label="FreightIQ" testId="admin-nav-freight" />
          <NavItem to="/admin/offers" icon={Tag} label="All Offers" testId="admin-nav-offers" />
          <NavItem to="/admin/purchase-orders" icon={FileUp} label="Purchase orders" testId="admin-nav-purchase-orders" />
          <NavItem to="/admin/inspection-reports" icon={ClipboardCheck} label="Inspection reports" testId="admin-nav-inspection-reports" />

          <SectionLabel label="System" />
          <NavItem to="/admin/content" icon={BookOpen} label="Content" testId="admin-nav-content" />
          <NavItem to="/admin/users" icon={UserCog} label="Users" testId="admin-nav-users" />
          <NavItem to="/admin/settings" icon={Settings} label="Settings" testId="admin-nav-settings" />
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-slate-200 flex-shrink-0">
          <button
            onClick={() => setUserMenuOpen(v => !v)}
            data-testid="admin-user-menu-button"
            className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">{initials}</span>
            </div>
            <div className="flex-1 text-left overflow-hidden">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400">Super Admin</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {userMenuOpen && (
            <div className="mt-1 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              <button
                onClick={handleLogout}
                data-testid="admin-logout-button"
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="ml-64 flex-1 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-10">
          <div />
          <div className="flex items-center gap-3">
            <button className="p-2 rounded-lg hover:bg-slate-50 transition-colors" data-testid="admin-notifications-button">
              <Bell className="w-5 h-5 text-slate-500" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center">
                <span className="text-white text-xs font-bold">{initials}</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-slate-900 leading-tight">{user?.name}</p>
                <p className="text-xs text-slate-400">Super Admin</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
